import React, { useMemo, useState } from "react";
import DashboardMrtTable from "./DashboardMrtTable";
import { DASHBOARD_EMPHASIS_CELL_SX } from "./dashboardTableStyles";
import {
  formatDaysCount,
  formatHoursCount,
  getPriorityTone,
} from "../../utils/dashboardUtils";
import TaskDetailsModal from "../TaskDetailsModal";
import AppDrawer from "../common/AppDrawer";

// Map MRT accessorKey → API sortBy field name
const SORT_KEY_MAP = {
  task: "title",
  owner: "assigned_to",
  tag: "project_name",
  priority: "priority",
  dueDate: "due_date",
  daysOverdue: "due_date",
  taskAge: "ageing",
};

const PriorityBadge = ({ priority }) => {
  const tone = getPriorityTone(priority);

  return (
    <span className={`dashboard-priority dashboard-priority--${tone}`}>
      <span className="dashboard-priority-dot" aria-hidden="true" />
      {priority}
    </span>
  );
};

const OverdueTasksTable = ({
  rows = [],
  isLoading = false,
  totalItems = 0,
  currentPage = 1,
  pageSize = 10,
  onPaginationChange,
  onSortChange,
  sortBy: externalSortBy = "ageing",
  sortOrder: externalSortOrder = "DESC",
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // MRT sorting state — start with ageing DESC to match initial API call
  const [sorting, setSorting] = useState([{ id: "taskAge", desc: true }]);

  const handleSortingChange = (updater) => {
    const newSorting = typeof updater === "function" ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortChange) {
      if (newSorting.length === 0) {
        onSortChange({ sortBy: "ageing", sortOrder: "DESC" });
      } else {
        const { id, desc } = newSorting[0];
        onSortChange({
          sortBy: SORT_KEY_MAP[id] ?? id,
          sortOrder: desc ? "DESC" : "ASC",
        });
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "task",
        header: "Task",
        size: 200,
        muiTableBodyCellProps: { sx: DASHBOARD_EMPHASIS_CELL_SX },
        Cell: ({ row }) => (
          <span
            onClick={() => {
              setSelectedTask({ id: row.original.id });
              setDrawerOpen(true);
            }}
            style={{ cursor: "pointer", color: "#2563eb", fontWeight: 600 }}
          >
            {row.original.task}
          </span>
        ),
      },
      {
        accessorKey: "owner",
        header: "Owner",
        size: 120,
      },
      {
        accessorKey: "tag",
        header: "Project",
        size: 150,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 120,
        Cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        size: 100,
      },
      {
        accessorKey: "daysOverdue",
        header: "Days Overdue",
        size: 120,
        muiTableBodyCellProps: { sx: DASHBOARD_EMPHASIS_CELL_SX },
        Cell: ({ row }) => formatDaysCount(row.original.daysOverdue),
      },
      {
        accessorKey: "taskAge",
        header: "Task Age",
        size: 100,
        Cell: ({ row }) => formatDaysCount(row.original.taskAge),
      },
      {
        accessorKey: "hours",
        header: "Hours",
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => formatHoursCount(row.original.hours),
      },
    ],
    []
  );

  return (
    <>
      <section className="dashboard-section dashboard-section--table">
        <h3 className="dashboard-section-title">All Overdue Tasks — Sorted by Age</h3>
        <DashboardMrtTable
          className="dashboard-overdue-table"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          emptyMessage="No overdue tasks."
          unitName="tasks"
          scrollContainer={true}
          enablePagination={true}
          enableServerSide={true}
          rowCount={totalItems}
          state={{
            pagination: { pageIndex: Math.max(0, currentPage - 1), pageSize },
            sorting,
          }}
          onPaginationChange={(updater) => {
            const newState = typeof updater === "function" ? updater({ pageIndex: Math.max(0, currentPage - 1), pageSize }) : updater;
            if (onPaginationChange) onPaginationChange({ page: newState.pageIndex + 1, limit: newState.pageSize });
          }}
          onSortingChange={handleSortingChange}
        />
      </section>

      <AppDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Task Details"
      >
        <TaskDetailsModal
          task={selectedTask}
          variant="drawer"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      </AppDrawer>
    </>
  );
};

export default OverdueTasksTable;


